import React, { useMemo, useState } from "react";
import IsoWorld from "@/three/IsoWorld";
import InfoPanel from "@/components/hud/InfoPanel";
import { useBuildings } from "@/lib/useBuildings";
import { findBuilding } from "@/three/helpers";
import { getUITheme, contrastColor } from "@/lib/uiTheme";
import { iconFor } from "@/lib/icons";
import { ArrowUpRight } from "lucide-react";

const theme = getUITheme(false);

export default function Embed() {
  const { buildings } = useBuildings();
  const [selected, setSelected] = useState(null);
  const [person, setPerson] = useState(null);
  const sorted = useMemo(
    () => [...buildings].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [buildings]
  );
  const current = findBuilding(sorted, selected);
  const panelSubject = person || current;
  const handleSelect = (slug) => { setPerson(null); setSelected(slug); };
  const closePanel = () => { if (person) setPerson(null); else setSelected(null); };

  return (
    <div className="relative h-screen w-screen overflow-hidden" style={{ background: theme.pageBg }} data-testid="embed-day">
      <IsoWorld buildings={sorted} selected={selected} onSelect={handleSelect} onPersonSelect={setPerson} />

      {/* compact brand chip */}
      <div className="absolute left-3 top-3 z-30 flex items-center gap-2 rounded-lg px-3 py-1.5" style={{ background: "#2B2E34", boxShadow: "var(--shadow-panel)" }}>
        <span className="font-display font-bold text-white text-base leading-none">S<span style={{ color: "#FFCE24" }}>/</span>M</span>
        <span className="font-display uppercase tracking-[0.18em] text-[9px] text-white/60">Iso World</span>
      </div>

      <a
        href="https://simon-myers.com"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute right-3 top-3 z-30 inline-flex items-center gap-1 rounded-lg px-3 py-2 font-display uppercase tracking-wide text-[10px] text-[#2B2E34] hover:brightness-95 transition-[filter]"
        style={{ background: "#FFCE24" }}
      >
        Visit site <ArrowUpRight size={13} />
      </a>

      <InfoPanel building={panelSubject} onClose={closePanel} compact theme={theme} />

      {/* compact area selector */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-30 w-[94%] max-w-[760px]">
        <div className="flex items-center gap-2 overflow-x-auto sm-scroll rounded-xl border px-2 py-2" style={{ background: theme.selectorBg, borderColor: theme.panelBorder, boxShadow: "var(--shadow-panel)" }}>
          <button
            data-testid="embed-area-overview"
            onClick={() => handleSelect(null)}
            className={`shrink-0 rounded-lg px-3 py-2 text-xs font-display font-semibold uppercase tracking-wide transition-colors ${!selected ? "" : theme.hoverClass}`}
            style={!selected ? { background: theme.itemActiveBg, color: theme.itemActiveText } : { color: theme.itemIdleText }}
          >
            Overview
          </button>
          {sorted.map((b) => {
            const active = selected === b.slug || selected === b.id;
            const Icon = iconFor(b.slug);
            return (
              <button
                key={b.id || b.slug}
                data-testid={`embed-area-${b.slug}`}
                onClick={() => handleSelect(b.slug)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-display font-semibold uppercase tracking-wide transition-colors ${active ? "" : theme.hoverClass}`}
                style={active ? { background: theme.itemActiveBg, color: theme.itemActiveText } : { color: theme.itemIdleText }}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-[5px] border" style={{ background: b.color, borderColor: "rgba(0,0,0,0.18)" }}>
                  <Icon size={11} color={contrastColor(b.color)} />
                </span>
                {b.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
